import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

/**
 * Outlet info interface
 */
export interface OutletInfo {
  id: string;
  name: string;
  storeName: string;
  color: string;
}

/**
 * Store info interface
 */
export interface StoreInfo {
  id: string;
  name: string;
}

/**
 * Multi-Outlet Context State Interface
 */
export interface MultiOutletContextState {
  // Outlet context
  outlets: OutletInfo[];

  // Comparison state
  comparisonMode: boolean;
  comparisonMetrics: string[];

  // Color assignments
  outletColors: Record<string, string>; // Outlet ID -> Color

  // Store context
  currentStore: StoreInfo | null;
}

// Predefined color palette for outlets
const COLOR_PALETTE = [
  '#1976d2', // blue
  '#388e3c', // green
  '#f57c00', // orange
  '#d32f2f', // red
  '#7b1fa2', // purple
  '#0097a7', // cyan
  '#c2185b', // pink
  '#5d4037', // brown
  '#616161', // grey
  '#fbc02d', // yellow
];

/**
 * Auto-assign colors to outlets
 */
const assignOutletColors = (outlets: OutletInfo[]): Record<string, string> => {
  const colors: Record<string, string> = {};
  outlets.forEach((outlet, index) => {
    colors[outlet.id] = outlet.color || COLOR_PALETTE[index % COLOR_PALETTE.length];
  });
  return colors;
};

/**
 * Initial state
 */
const initialState: MultiOutletContextState = {
  outlets: [],
  comparisonMode: false,
  comparisonMetrics: [],
  outletColors: {},
  currentStore: null,
};

/**
 * Multi-Outlet Context Slice
 */
export const multiOutletContextSlice = createSlice({
  name: 'multiOutletContext',
  initialState,
  reducers: {
    // Set available outlets
    setOutlets: (state, action: PayloadAction<OutletInfo[]>) => {
      state.outlets = action.payload;
      // Auto-assign colors
      state.outletColors = assignOutletColors(action.payload);
    },

    // Add a single outlet
    addOutlet: (state, action: PayloadAction<OutletInfo>) => {
      const exists = state.outlets.some(o => o.id === action.payload.id);
      if (!exists) {
        state.outlets.push(action.payload);
        // Assign color if not provided
        if (!action.payload.color) {
          const index = state.outlets.length - 1;
          state.outletColors[action.payload.id] = COLOR_PALETTE[index % COLOR_PALETTE.length];
        } else {
          state.outletColors[action.payload.id] = action.payload.color;
        }
      }
    },

    // Remove an outlet
    removeOutlet: (state, action: PayloadAction<string>) => {
      state.outlets = state.outlets.filter(o => o.id !== action.payload);
      delete state.outletColors[action.payload];
    },

    // Update outlet info
    updateOutlet: (state, action: PayloadAction<{ id: string; updates: Partial<OutletInfo> }>) => {
      const index = state.outlets.findIndex(o => o.id === action.payload.id);
      if (index !== -1) {
        state.outlets[index] = { ...state.outlets[index], ...action.payload.updates };
        // Update color if changed
        if (action.payload.updates.color) {
          state.outletColors[action.payload.id] = action.payload.updates.color;
        }
      }
    },

    // Enable/disable comparison mode
    setComparisonMode: (state, action: PayloadAction<boolean>) => {
      state.comparisonMode = action.payload;
      // Clear comparison metrics when disabling
      if (!action.payload) {
        state.comparisonMetrics = [];
      }
    },

    // Set which metrics to compare
    setComparisonMetrics: (state, action: PayloadAction<string[]>) => {
      state.comparisonMetrics = action.payload;
    },

    // Add a comparison metric
    addComparisonMetric: (state, action: PayloadAction<string>) => {
      if (!state.comparisonMetrics.includes(action.payload)) {
        state.comparisonMetrics.push(action.payload);
      }
    },

    // Remove a comparison metric
    removeComparisonMetric: (state, action: PayloadAction<string>) => {
      state.comparisonMetrics = state.comparisonMetrics.filter(m => m !== action.payload);
    },

    // Assign color to outlet
    setOutletColor: (state, action: PayloadAction<{ outletId: string; color: string }>) => {
      state.outletColors[action.payload.outletId] = action.payload.color;
      // Update outlet's color in the outlets array
      const outlet = state.outlets.find(o => o.id === action.payload.outletId);
      if (outlet) {
        outlet.color = action.payload.color;
      }
    },

    // Set current store context
    setCurrentStore: (state, action: PayloadAction<StoreInfo | null>) => {
      state.currentStore = action.payload;
    },

    // Reset all context
    resetMultiOutletContext: (state) => {
      state.outlets = [];
      state.comparisonMode = false;
      state.comparisonMetrics = [];
      state.outletColors = {};
      state.currentStore = null;
    },
  },
  extraReducers: (builder) => {
    // Clear context on logout
    builder.addMatcher(
      (action) => action.type === 'auth/logout/fulfilled' || action.type === 'auth/clearAuth',
      () => initialState
    );
  },
});

// Actions
export const {
  setOutlets,
  addOutlet,
  removeOutlet,
  updateOutlet,
  setComparisonMode,
  setComparisonMetrics,
  addComparisonMetric,
  removeComparisonMetric,
  setOutletColor,
  setCurrentStore,
  resetMultiOutletContext,
} = multiOutletContextSlice.actions;

// Selectors
export const selectMultiOutletContext = (state: RootState) => state.multiOutletContext;
export const selectOutlets = (state: RootState) => state.multiOutletContext.outlets;
export const selectOutletById = (outletId: string) => (state: RootState) =>
  state.multiOutletContext.outlets.find(o => o.id === outletId);
export const selectOutletColor = (outletId: string) => (state: RootState) =>
  state.multiOutletContext.outletColors[outletId] || COLOR_PALETTE[0];
export const selectComparisonMetrics = (state: RootState) => state.multiOutletContext.comparisonMetrics;
export const selectCurrentStore = (state: RootState) => state.multiOutletContext.currentStore;
export const selectIsComparisonMode = (state: RootState) => state.multiOutletContext.comparisonMode;

// Get outlets by IDs
export const selectOutletsByIds = (outletIds: string[]) => (state: RootState) =>
  state.multiOutletContext.outlets.filter(o => outletIds.includes(o.id));

// Get color map for selected outlets
export const selectOutletColorMap = (outletIds: string[]) => (state: RootState) => {
  const colorMap: Record<string, string> = {};
  outletIds.forEach(id => {
    colorMap[id] = state.multiOutletContext.outletColors[id] || COLOR_PALETTE[0];
  });
  return colorMap;
};

export default multiOutletContextSlice.reducer;
